import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  FiChevronLeft,
  FiChevronRight,
  FiHeart,
  FiSliders,
  FiX,
} from "react-icons/fi";
import { Link, useSearchParams } from "react-router-dom";
import "../css/shop.scss";
import productData, {
  formatPrice,
  productCategories,
  siteConfig,
} from "../Datas/productData";
import { CartContext } from "./CartContext";

const fallbackImage =
  "data:image/svg+xml;charset=UTF-8," +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 5 6"><rect width="5" height="6" fill="#efefed"/><path d="M1.5 3h2l.2 1.5h-2.4z" fill="none" stroke="#777" stroke-width=".08"/><path d="M2 3c0-.6 1-.6 1 0" fill="none" stroke="#777" stroke-width=".08"/></svg>'
  );

const PRODUCTS_PER_PAGE = 20;

const getPageNumbers = (totalPages, currentPage) => {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  const pages = new Set([
    1,
    totalPages,
    currentPage - 1,
    currentPage,
    currentPage + 1,
  ]);
  const orderedPages = [...pages]
    .filter((page) => page >= 1 && page <= totalPages)
    .sort((a, b) => a - b);

  return orderedPages.reduce((items, page, index) => {
    const previousPage = orderedPages[index - 1];
    if (previousPage && page - previousPage > 1) items.push("ellipsis");
    items.push(page);
    return items;
  }, []);
};

const getDiscount = (product) =>
  product.compareAtPrice
    ? Math.round((1 - product.price / product.compareAtPrice) * 100)
    : 0;

const colorFamily = (product) => {
  const value = product.colors
    .map((item) => item.name)
    .join(" ")
    .toLowerCase();
  if (/black|ink|coal|graphite|night/.test(value)) return "Black";
  if (/blue|denim|indigo|silver/.test(value)) return "Blue";
  if (/brown|cognac|espresso|mahogany|sand|oat/.test(value)) return "Brown";
  if (/cream|milk|chalk|stone|grey|mushroom/.test(value)) return "Neutral";
  return "Statement";
};

export const ProductCard = ({ product, priority = false }) => {
  const { wishlist, toggleWishlist } = useContext(CartContext);
  const saved = wishlist.includes(product.id);
  const discount = getDiscount(product);

  return (
    <article className="product-card">
      <div className="product-card__media">
        <Link
          to={`/shopDetails/${product.id}`}
          aria-label={`View ${product.name}`}
          onClick={() => window.scrollTo(0, 0)}
        >
          <img
            src={product.image}
            alt={product.imageAlt}
            loading={priority ? "eager" : "lazy"}
            decoding="async"
            onError={(event) => {
              event.currentTarget.onerror = null;
              event.currentTarget.src = fallbackImage;
            }}
          />
        </Link>
        <div className="product-card__badges">
          {product.status === "new" && <span>New</span>}
          {discount > 0 && <span>-{discount}%</span>}
        </div>
        <button
          type="button"
          className={`product-card__wish ${saved ? "is-saved" : ""}`}
          onClick={() => toggleWishlist(product.id)}
          aria-label={
            saved
              ? `Remove ${product.name} from wishlist`
              : `Add ${product.name} to wishlist`
          }
          aria-pressed={saved}
        >
          <FiHeart aria-hidden="true" />
        </button>
      </div>
      <div className="product-card__content">
        <Link
          to={`/shopDetails/${product.id}`}
          onClick={() => window.scrollTo(0, 0)}
        >
          <h2>{product.name}</h2>
        </Link>
        <p className="product-card__type">{product.type}</p>
        <div className="product-card__price">
          <span className={product.compareAtPrice ? "is-sale" : ""}>
            {formatPrice(product.price)}
          </span>
          {product.compareAtPrice && (
            <del>{formatPrice(product.compareAtPrice)}</del>
          )}
        </div>
        <div className="product-card__colors" aria-label="Available colors">
          {product.colors.slice(0, 4).map((item) => (
            <span
              key={item.name}
              title={item.name}
              style={{ "--swatch": item.hex }}
            />
          ))}
          <small>{product.colors.length} colors</small>
        </div>
      </div>
    </article>
  );
};

function Shop() {
  const { wishlist } = useContext(CartContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const [filterOpen, setFilterOpen] = useState(false);
  const [selectedColor, setSelectedColor] = useState("All");
  const [selectedMaterial, setSelectedMaterial] = useState("All");
  const [selectedModel, setSelectedModel] = useState("All");
  const [maxPrice, setMaxPrice] = useState(450);
  const [sortBy, setSortBy] = useState("relevance");

  const category = searchParams.get("category") || "All";
  const collection = searchParams.get("collection") || "";
  const search = searchParams.get("search")?.trim() || "";
  const view = searchParams.get("view") || "";
  const requestedPage = Number(searchParams.get("page")) || 1;

  useEffect(() => {
    document.title =
      view === "wishlist"
        ? `Wishlist | ${siteConfig.displayName}`
        : search
        ? `Search “${search}” | ${siteConfig.displayName}`
        : `Bags | ${siteConfig.displayName} ${siteConfig.brandLine}`;
  }, [search, view]);

  const materials = ["All", "Leather", "Denim", "Canvas"];
  const models = ["All", ...new Set(productData.map((item) => item.type))];
  const colors = ["All", "Black", "Brown", "Blue", "Neutral", "Statement"];

  const visibleProducts = useMemo(() => {
    const normalizedSearch = search.toLowerCase();
    const matches = productData.filter((item) => {
      const searchable = [
        item.name,
        item.type,
        item.category,
        item.description,
        ...item.tags,
      ]
        .join(" ")
        .toLowerCase();
      const categoryMatch = category === "All" || item.category === category;
      const normalizedCollection = collection.toLowerCase();
      const collectionMatch =
        !normalizedCollection ||
        normalizedCollection === "new" ||
        item.status === normalizedCollection;
      const searchMatch =
        !normalizedSearch || searchable.includes(normalizedSearch);
      const colorMatch =
        selectedColor === "All" || colorFamily(item) === selectedColor;
      const materialMatch =
        selectedMaterial === "All" ||
        item.material.toLowerCase().includes(selectedMaterial.toLowerCase());
      const modelMatch = selectedModel === "All" || item.type === selectedModel;
      const wishlistMatch = view !== "wishlist" || wishlist.includes(item.id);
      return (
        categoryMatch &&
        collectionMatch &&
        searchMatch &&
        colorMatch &&
        materialMatch &&
        modelMatch &&
        wishlistMatch &&
        item.price <= maxPrice
      );
    });

    if (sortBy === "price-low") {
      return [...matches].sort((a, b) => a.price - b.price);
    }
    if (sortBy === "price-high") {
      return [...matches].sort((a, b) => b.price - a.price);
    }
    if (sortBy === "new") {
      return [...matches].sort(
        (a, b) => (b.status === "new") - (a.status === "new") || b.id - a.id
      );
    }
    return matches;
  }, [
    category,
    collection,
    maxPrice,
    search,
    selectedColor,
    selectedMaterial,
    selectedModel,
    sortBy,
    view,
    wishlist,
  ]);

  const activeFilterCount = [
    selectedColor !== "All",
    selectedMaterial !== "All",
    selectedModel !== "All",
    maxPrice < 450,
  ].filter(Boolean).length;

  const totalPages = Math.max(
    1,
    Math.ceil(visibleProducts.length / PRODUCTS_PER_PAGE)
  );
  const currentPage = Math.min(
    Math.max(1, Math.trunc(requestedPage)),
    totalPages
  );
  const pageStart = (currentPage - 1) * PRODUCTS_PER_PAGE;
  const paginatedProducts = visibleProducts.slice(
    pageStart,
    pageStart + PRODUCTS_PER_PAGE
  );
  const pageNumbers = getPageNumbers(totalPages, currentPage);

  const resetPageParam = () => {
    if (!searchParams.has("page")) return;
    const next = new URLSearchParams(searchParams);
    next.delete("page");
    setSearchParams(next, { replace: true });
  };

  const goToPage = (nextPage) => {
    const normalizedPage = Math.min(Math.max(nextPage, 1), totalPages);
    if (normalizedPage === currentPage) return;

    const next = new URLSearchParams(searchParams);
    if (normalizedPage === 1) next.delete("page");
    else next.set("page", String(normalizedPage));
    setSearchParams(next);

    const toolbar = document.querySelector(".shop-page__toolbar");
    if (typeof toolbar?.scrollIntoView === "function") {
      toolbar.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const chooseCategory = (nextCategory) => {
    const next = new URLSearchParams(searchParams);
    if (nextCategory === "All") next.delete("category");
    else next.set("category", nextCategory);
    next.delete("page");
    setSearchParams(next);
  };

  const resetFilters = () => {
    setSelectedColor("All");
    setSelectedMaterial("All");
    setSelectedModel("All");
    setMaxPrice(450);
    resetPageParam();
  };

  const pageTitle =
    view === "wishlist"
      ? "Wishlist"
      : search
      ? `Search results for “${search}”`
      : collection
      ? collection
      : category === "All"
      ? "Bags"
      : category;

  return (
    <main className="shop-page">
      <nav className="shop-page__breadcrumb" aria-label="Breadcrumb">
        <Link to="/">Home</Link>
        <span>/</span>
        <span>{pageTitle}</span>
      </nav>

      <header className="shop-page__intro">
        <h1>{pageTitle}</h1>
        <p className="shop-page__count">
          {visibleProducts.length === 0
            ? "0 items"
            : `Showing ${pageStart + 1}–${
                pageStart + paginatedProducts.length
              } of ${visibleProducts.length} items`}
        </p>
        <p>
          Characterful, adaptable companions for every version of the day —
          designed with clean lines, tactile materials and thoughtful interiors.
        </p>
      </header>

      <nav className="shop-page__categories" aria-label="Bag categories">
        {productCategories.map((item) => (
          <button
            type="button"
            key={item}
            className={category === item ? "is-active" : ""}
            onClick={() => chooseCategory(item)}
          >
            {item}
          </button>
        ))}
      </nav>

      <div className="shop-page__toolbar">
        <button
          type="button"
          className="filter-trigger"
          onClick={() => setFilterOpen(true)}
          aria-expanded={filterOpen}
        >
          <FiSliders aria-hidden="true" />
          All filters
          {activeFilterCount > 0 && <span>{activeFilterCount}</span>}
        </button>
        <label>
          <span>Sort by:</span>
          <select
            value={sortBy}
            onChange={(event) => {
              setSortBy(event.target.value);
              resetPageParam();
            }}
          >
            <option value="relevance">Relevance</option>
            <option value="new">Newest</option>
            <option value="price-low">Price low to high</option>
            <option value="price-high">Price high to low</option>
          </select>
        </label>
      </div>

      <div className="shop-page__grid" aria-live="polite">
        {paginatedProducts.map((item, index) => (
          <ProductCard key={item.id} product={item} priority={index < 4} />
        ))}
      </div>

      {visibleProducts.length === 0 && (
        <section className="shop-page__empty">
          <h2>No pieces found</h2>
          <p>Try a broader search or clear the active filters.</p>
          <button type="button" onClick={resetFilters}>
            Clear filters
          </button>
        </section>
      )}

      {visibleProducts.length > PRODUCTS_PER_PAGE && (
        <nav className="shop-page__pagination" aria-label="Pagination">
          <button
            type="button"
            className="pagination-direction"
            onClick={() => goToPage(currentPage - 1)}
            disabled={currentPage === 1}
            aria-label="Previous page"
          >
            <FiChevronLeft aria-hidden="true" />
            <span>Previous</span>
          </button>
          {pageNumbers.map((page, index) =>
            page === "ellipsis" ? (
              <span className="pagination-ellipsis" key={`ellipsis-${index}`}>
                …
              </span>
            ) : (
              <button
                type="button"
                key={page}
                className={page === currentPage ? "is-active" : ""}
                aria-current={page === currentPage ? "page" : undefined}
                aria-label={`Page ${page}`}
                onClick={() => goToPage(page)}
              >
                {page}
              </button>
            )
          )}
          <button
            type="button"
            className="pagination-direction"
            onClick={() => goToPage(currentPage + 1)}
            disabled={currentPage === totalPages}
            aria-label="Next page"
          >
            <span>Next</span>
            <FiChevronRight aria-hidden="true" />
          </button>
        </nav>
      )}

      <button
        type="button"
        className={`filter-backdrop ${filterOpen ? "is-open" : ""}`}
        onClick={() => setFilterOpen(false)}
        aria-label="Close filters"
        tabIndex={filterOpen ? 0 : -1}
      />
      <aside
        className={`filter-drawer ${filterOpen ? "is-open" : ""}`}
        aria-hidden={!filterOpen}
        aria-label="Product filters"
      >
        <div className="filter-drawer__header">
          <h2>All filters</h2>
          <button
            type="button"
            onClick={() => setFilterOpen(false)}
            aria-label="Close filters"
          >
            <FiX aria-hidden="true" />
          </button>
        </div>
        <div className="filter-drawer__body">
          <fieldset>
            <legend>Color</legend>
            <div className="filter-options">
              {colors.map((item) => (
                <label key={item}>
                  <input
                    type="radio"
                    name="color"
                    checked={selectedColor === item}
                    onChange={() => {
                      setSelectedColor(item);
                      resetPageParam();
                    }}
                  />
                  <span>{item}</span>
                </label>
              ))}
            </div>
          </fieldset>
          <fieldset>
            <legend>Material</legend>
            <select
              value={selectedMaterial}
              onChange={(event) => {
                setSelectedMaterial(event.target.value);
                resetPageParam();
              }}
            >
              {materials.map((item) => (
                <option key={item}>{item}</option>
              ))}
            </select>
          </fieldset>
          <fieldset>
            <legend>Model</legend>
            <select
              value={selectedModel}
              onChange={(event) => {
                setSelectedModel(event.target.value);
                resetPageParam();
              }}
            >
              {models.map((item) => (
                <option key={item}>{item}</option>
              ))}
            </select>
          </fieldset>
          <fieldset>
            <legend>Price up to {formatPrice(maxPrice)}</legend>
            <input
              type="range"
              min="50"
              max="450"
              step="10"
              value={maxPrice}
              onChange={(event) => {
                setMaxPrice(Number(event.target.value));
                resetPageParam();
              }}
            />
          </fieldset>
        </div>
        <div className="filter-drawer__footer">
          <button type="button" className="secondary" onClick={resetFilters}>
            Reset
          </button>
          <button type="button" onClick={() => setFilterOpen(false)}>
            Show {visibleProducts.length} items
          </button>
        </div>
      </aside>
    </main>
  );
}

export default Shop;
